<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import { useAnalyticsIdentity } from '@/composables/useAnalyticsIdentity'
import { useAuth } from '@/composables/useAuth'
import { useAuthEffects } from '@/composables/useAuthEffects'
import { useAuthTabSync } from '@/composables/useAuthTabSync'
import { useIdleTimeout } from '@/composables/useIdleTimeout'
import SMediaDialog from '@/components/singleton/SMediaDialog/SMediaDialog.vue'
import SMessageDialog from '@/components/singleton/SMessageDialog/SMessageDialog.vue'
import SProfileDialog from '@/components/singleton/SProfileDialog/SProfileDialog.vue'
import SRespiteDialog from '@/components/singleton/SRespiteDialog/SRespiteDialog.vue'
import SAmortizationDialog from '@/components/singleton/SAmortizationDialog/SAmortizationDialog.vue'
import SPaymentDialog from '@/components/singleton/SPaymentDialog/SPaymentDialog.vue'
import SNavigation from '@/components/singleton/SNavigation/SNavigation.vue'
import SSnackbar from '@/components/singleton/SSnackbar/SSnackbar.vue'

const route = useRoute()

useIdleTimeout()
useAuthTabSync()
useAuthEffects()
useAnalyticsIdentity()
const { isAuthenticated } = useAuth()

const loginView = computed(() => route.name === 'Authenticate')
</script>

<template>
  <v-app id="app">
    <!-- Unmounted (not just hidden) when logged out: the drawer is local state and would otherwise stay
         open and clickable after an idle logout. Every route where the nav shows requires auth. -->
    <s-navigation v-if="isAuthenticated" />
    <v-main>
      <!-- pa-0 on the login route: the mobile photo strip is full-bleed (ADA-4497);
           the view owns its own paddings. -->
      <v-container
        :class="{ 'app-width': !loginView, 'fill-height': loginView, 'pa-0': loginView }"
        :fluid="loginView"
        class="position-relative"
      >
        <!-- Singleton dialogs: store-driven, closed on auth transitions via
             dialogsStore.reset() in useAuthEffects. -->
        <s-media-dialog v-if="isAuthenticated" />
        <s-message-dialog v-if="isAuthenticated" />
        <s-profile-dialog v-if="isAuthenticated" />
        <s-respite-dialog v-if="isAuthenticated" />
        <s-amortization-dialog v-if="isAuthenticated" />

        <!-- NOT auth-gated, unlike its siblings: pay-with-OCR is reached from the login screen
             (ADA-4432 D4). Where the legacy Payments needed a `:key` remount to close itself on
             an auth transition, this one is store-driven — dialogsStore.reset() closes it. -->
        <s-payment-dialog />

        <router-view />
        <s-snackbar />
      </v-container>
    </v-main>
  </v-app>
</template>

<style>
/* Below the md breakpoint the main area no longer grows to fill the viewport height. */
@media (max-width: 960px) {
  .v-main {
    flex: 0;
  }
}
</style>
